import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useScriptCreation } from '../../contexts/ScriptCreationContext';
import { useWebAudio } from '../../hooks/useWebAudio';
import { config } from '../../config';
import { HelpButton } from '../../components/common/HelpButton';
import { Icon } from '../../components/common/Icon';
import { FaPlay, FaPause, FaStop } from 'react-icons/fa';

const Container = styled.div`
  margin: 0 auto;
  max-width: 800px;
`;

const Card = styled.div`
  background-color: #2a2a2a;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: #ffffff;
  margin: 0 0 8px 0;
`;

const Description = styled.p`
  color: #aaaaaa;
  margin: 0 0 20px 0;
  font-size: 15px;
`;

const SummaryBox = styled.div`
  background-color: #1f2a3a;
  border-left: 4px solid #667eea;
  border-radius: 8px;
  padding: 15px 18px;
  color: #cccccc;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const TrackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const TrackRow = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background-color: #1f1f1f;
  border-radius: 8px;
  border-left: 4px solid ${props => props.$color};
  color: #dddddd;
  font-size: 14px;
`;

const TrackMeta = styled.span`
  color: #888888;
  font-size: 12px;
  margin-left: auto;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 10px;
`;

const PlayButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { background: #666; cursor: not-allowed; }
`;

const StopButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid #555;
  background: none;
  color: #cccccc;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) { border-color: #007bff; color: #ffffff; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const TimeLabel = styled.span`
  color: #cccccc;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
`;

const LoadingNote = styled.div`
  color: #888888;
  font-size: 14px;
  margin-bottom: 10px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  align-items: center;
`;

const PrimaryButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  padding: 14px 26px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { background-color: #218838; }
  &:disabled { background-color: #666; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
  background: none;
  border: 1px solid #444;
  color: #cccccc;
  padding: 14px 22px;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;

  &:hover { border-color: #007bff; color: #ffffff; }
`;

const ErrorMessage = styled.div`
  background-color: rgba(220, 53, 69, 0.15);
  border: 1px solid #dc3545;
  color: #ff8b7d;
  border-radius: 8px;
  padding: 12px 15px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const StatusNote = styled.div`
  color: #7bb8ff;
  font-size: 14px;
  margin-top: 12px;
`;

const helpContent = `
<div class="alert info">
Your meditation is fully assembled!
</br></br>
<ul>
<li>Press play to listen to the complete mix (voice + AI-selected background audio).</li>
<li>Happy with it? Finalize & continue to publish.</li>
<li>Want tweaks? Open the manual mixer to adjust any track.</li>
</ul>
</div>
`;

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const ZenSummary: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    scriptName,
    publishDescription,
    zenSettings,
    audioMixData,
    loadAudioMix,
    scriptAudioFile,
    scriptDbId,
    setScriptFinalAudioFile,
    generateScriptMetadata,
    selectedParts
  } = useScriptCreation();

  const {
    isInitialized,
    isPlaying,
    currentTime,
    duration,
    tracks,
    initializeAudio,
    importMixData,
    play,
    pause,
    stop,
    renderMixToFile
  } = useWebAudio();

  const [error, setError] = useState<string | null>(null);
  const [isLoadingMix, setIsLoadingMix] = useState(true);
  const [mixFetchDone, setMixFetchDone] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeStatus, setFinalizeStatus] = useState('');
  const mixLoadedRef = useRef(false);

  // Load the saved mix data from the backend
  useEffect(() => {
    (async () => {
      try {
        await loadAudioMix();
      } catch (e) {
        console.error('Failed to fetch audio mix:', e);
      }
      setMixFetchDone(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize web audio on mount
  useEffect(() => {
    initializeAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Import the mix once audio is initialized and mix data is available
  useEffect(() => {
    const importMix = async () => {
      if (!isInitialized || !audioMixData || mixLoadedRef.current) return;
      mixLoadedRef.current = true;
      try {
        const scriptAudioUrl = scriptAudioFile ? `${config.apiUrl}${scriptAudioFile}` : undefined;
        await importMixData(audioMixData, scriptAudioUrl, config.apiUrl);
        setIsLoadingMix(false);
      } catch (e) {
        console.error('Failed to load the audio mix:', e);
        mixLoadedRef.current = false;
        setIsLoadingMix(false);
        setError('Failed to load the audio mix for playback. You can still open it in the manual mixer.');
      }
    };
    importMix();
  }, [isInitialized, audioMixData, importMixData, scriptAudioFile]);

  // Resolve the loading state when the fetch finished but produced no mix data
  useEffect(() => {
    if (mixFetchDone && !audioMixData && isLoadingMix) {
      setIsLoadingMix(false);
      if (zenSettings?.stage === 'mix_generated') {
        setError('The AI mix could not be loaded. You can open it in the manual mixer, or go back to the preview and regenerate it.');
      }
    }
  }, [mixFetchDone, audioMixData, isLoadingMix, zenSettings]);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  // Simplified version of the manual Audio page finalize: render -> S3 -> metadata -> publish
  const handleFinalizeAndPublish = useCallback(async () => {
    if (!scriptDbId || isFinalizing) return;
    setError(null);
    setIsFinalizing(true);
    try {
      if (isPlaying) stop();

      setFinalizeStatus('Rendering the final mix…');
      const audioBlob = await renderMixToFile();

      setFinalizeStatus('Uploading…');
      let fileExtension = 'mp4';
      if (audioBlob.type.includes('audio/mp4') || audioBlob.type.includes('audio/aac')) {
        fileExtension = 'mp4';
      } else if (audioBlob.type.includes('audio/mpeg') || audioBlob.type.includes('audio/mp3')) {
        fileExtension = 'mp3';
      }

      const presignedResponse = await fetch(`${config.apiUrl}/generate-s3-presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ scriptId: scriptDbId, fileExtension })
      });
      if (!presignedResponse.ok) throw new Error('Failed to get upload URL');
      const { presignedUrl, key } = await presignedResponse.json();

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', 'audio/mp4');
        xhr.send(audioBlob);
      });

      setFinalizeStatus('Saving…');
      const saveMetadataResponse = await fetch(`${config.apiUrl}/scripts/${scriptDbId}/save-audio-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ s3Key: key, duration: Math.round(duration) })
      });
      if (!saveMetadataResponse.ok) throw new Error('Failed to save audio metadata');

      setScriptFinalAudioFile(key);

      setFinalizeStatus('Preparing publish details…');
      try {
        await generateScriptMetadata();
      } catch (e) {
        console.warn('Metadata generation failed (non-fatal):', e);
      }

      const basePath = location.pathname.includes('/edit/') ? `/edit/${scriptDbId}` : '/create';
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`${basePath}/publish`);
    } catch (e: any) {
      console.error('Finalize failed:', e);
      setError(e?.message || 'Failed to finalize the meditation.');
    } finally {
      setIsFinalizing(false);
      setFinalizeStatus('');
    }
  }, [scriptDbId, isFinalizing, isPlaying, stop, renderMixToFile, duration, setScriptFinalAudioFile, generateScriptMetadata, location.pathname, navigate]);

  if (!zenSettings || zenSettings.stage !== 'mix_generated') {
    return (
      <Container>
        <Card>
          <Title>Almost there…</Title>
          <Description>
            The AI audio mix hasn't been generated yet. Head back to the preview to generate speech and the mix.
          </Description>
          <SecondaryButton onClick={() => navigate('../zen-preview')}>Back to Preview</SecondaryButton>
        </Card>
      </Container>
    );
  }

  const playableReady = isInitialized && !isLoadingMix && tracks.length > 0;

  return (
    <Container>
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Card>
        <Title>{scriptName || 'Your Meditation'}</Title>
        {publishDescription && <Description>{publishDescription}</Description>}

        {zenSettings.mixSummary && (
          <SummaryBox>
            <strong>Your AI audio engineer says:</strong><br />
            {zenSettings.mixSummary}
          </SummaryBox>
        )}

        {tracks.length > 0 && (
          <TrackList>
            {tracks.map(track => (
              <TrackRow key={track.id} $color={track.color || '#4caf50'}>
                {track.name}
                <TrackMeta>
                  {track.type === 'main' ? 'voice' : track.type}
                  {typeof track.volume === 'number' ? ` · vol ${Math.round(track.volume * 100)}%` : ''}
                </TrackMeta>
              </TrackRow>
            ))}
          </TrackList>
        )}

        {!playableReady && <LoadingNote>Loading your mix…</LoadingNote>}

        <Controls>
          <PlayButton onClick={handlePlayPause} disabled={!playableReady || isFinalizing}>
            <Icon icon={isPlaying ? FaPause : FaPlay} />
          </PlayButton>
          <StopButton onClick={stop} disabled={!playableReady || isFinalizing}>
            <Icon icon={FaStop} />
          </StopButton>
          <TimeLabel>{formatTime(currentTime)} / {formatTime(duration)}</TimeLabel>
        </Controls>
      </Card>

      <Card>
        <ButtonRow>
          <PrimaryButton onClick={handleFinalizeAndPublish} disabled={!playableReady || isFinalizing}>
            {isFinalizing ? (finalizeStatus || 'Finalizing…') : 'Finalize & Continue to Publish'}
          </PrimaryButton>
          <SecondaryButton onClick={() => navigate('../audio')} disabled={isFinalizing}>
            Fine-tune in Manual Mixer
          </SecondaryButton>
        </ButtonRow>
        {isFinalizing && <StatusNote>{finalizeStatus}</StatusNote>}
      </Card>

      <HelpButton
        title="Intelligent Zen Mode - Summary"
        content={helpContent}
        position="fixed-bottom-right"
      />
    </Container>
  );
};

export default ZenSummary;
